import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import type { RecoverRequest } from './api'
import { Button } from '@/components/ui/button'
import { FileJson, List, ListTree, Map, UserCheck, FileStack, QrCode, Info, CheckCircle2 } from 'lucide-react'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { buildIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { formatDateForLocale } from '@/lib/dateUtils'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { useRowScenarioInfo } from './hooks'
import { effectiveIsGo, waitGoTitle, WaitGoCaveatBadge } from '@/features/recover-requests/components/WaitGoState'

// Helper function to normalize MySQL DATETIME strings to UTC ISO format
// MySQL returns dates as "2026-01-23 09:57:00" (no timezone), but they're stored as UTC
const normalizeUtcDate = (datetime: string | Date | null | undefined): string | null => {
    if (!datetime) return null
    if (datetime instanceof Date) {
        return datetime.toISOString()
    }
    if (typeof datetime === 'string') {
        // Convert MySQL format "2026-01-23 09:57:00" to ISO "2026-01-23T09:57:00Z"
        let dateStr = datetime.includes('T') ? datetime : datetime.replace(' ', 'T')
        
        // If no timezone info, treat as UTC (append 'Z')
        if (!dateStr.includes('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
            dateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
        }
        
        return dateStr
    }
    return null
}

// Helper function to format date/time with locale support
// Uses existing formatDateForLocale utility which handles UTC to local conversion
const formatDate = (datetime: string | Date | null | undefined, locale: string = 'en-GB') => {
    const normalizedDate = normalizeUtcDate(datetime)
    if (!normalizedDate) return '-'
    
    try {
        return formatDateForLocale(normalizedDate, locale, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        })
    } catch (error) {
        console.warn('Failed to format date:', error)
        return '-'
    }
}

const getStatusCircle = (analytics: number | null) => (
    <AnalyticsStatusIndicator status={analytics} size="h-4 w-4" />
)

function RowScenarioInfoIcon({
    requestID,
    scenario,
    locale,
    t,
    onOpenModal,
}: {
    requestID: string | null
    scenario: number | null
    locale: string
    t: (key: string) => string
    onOpenModal: () => void
}) {
    const [hovered, setHovered] = useState(false)
    const { data, isLoading } = useRowScenarioInfo(requestID, scenario, hovered)

    const requestSetLines = (data?.requestSets ?? []).map((row) => {
        const obj = row.objectID ?? ''
        const formatted = formatDate(row.requestDate, locale)
        return `Part of ${obj}, ${formatted}`.trimEnd()
    })

    const tooltipContent = isLoading
        ? t('common.loading', 'Loading...')
        : (
            <div className="space-y-2 text-left text-xs max-w-sm">
                <div className="font-semibold border-b pb-1.5">{t('recoverRequests.scenarioAndRequestInfo', 'Scenario and Request Information')}</div>
                {data?.scenario && (
                    <>
                        <div className="font-medium">{data.scenario.scenarioName ?? '-'}</div>
                        {data.scenario.description != null && data.scenario.description !== '' && (
                            <p className="text-muted-foreground"><TranslatableText text={data.scenario.description} /></p>
                        )}
                        {data.scenario.useCase != null && data.scenario.useCase !== '' && (
                            <p><span className="font-medium">{t('recoverRequests.useCase', 'Use case')}:</span> {data.scenario.useCase}</p>
                        )}
                    </>
                )}
                {requestSetLines.length > 0 && (
                    <div>
                        <div className="font-medium">{t('recoverRequests.requestSets', 'Request sets')}</div>
                        <ul className="list-disc list-inside mt-1">{requestSetLines.map((line, i) => <li key={i}>{line}</li>)}</ul>
                    </div>
                )}
            </div>
        )

    if (requestID == null) return null

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 min-w-0 flex-shrink-0"
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        onClick={(e) => {
                            e.stopPropagation()
                            onOpenModal()
                        }}
                        aria-label={t('recoverRequests.scenarioInfo', 'Scenario information')}
                    >
                        <Info className="h-3.5 w-3.5" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-sm">
                    {tooltipContent}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}

export function createRecoverRequestColumns(
    t: (key: string) => string,
    locale: string = 'en-GB',
    onShowMetadata?: (title: string, content: string | null) => void,
    onShowRunbook?: (objectID: string, scenario: number | null, forecastStartTime: string | Date | null) => void,
    onShowFloorPlan?: (objectID: string) => void,
    onAssignEngineer?: (objectID: string, recoverRequestId: number | null, waitGo: string | null, analytics: number | null, responsibleTeamId: number | null, locationId: number | null) => void,
    onShowObjectDetails?: (objectID: string, requestID: string) => void,
    onShowQRCode?: (objectID: string) => void,
    onShowSubObjectProgress?: (mainId: string, objectDescription?: string | null) => void,
    onShowScenarioInfo?: (requestID: string | null, scenario: number | null, objectID?: string | null, objectDescription?: string | null) => void,
    onMarkComplete?: (objectID: string, objectDescription: string | null, recoverRequestId: number | null) => void,
    /**
     * Command and control's policy: may an engineer work around a bypassable dependency?
     * Defaults to the strict reading, matching the screen — a caller that forgets to pass it
     * should under-promise rather than show work as assignable that nobody agreed to bypass.
     */
    allowBypass: boolean = false
): ColumnDef<RecoverRequest>[] {
    return [
        {
            accessorKey: 'objectID',
            header: t('recoverRequests.objectID'),
            size: 260,
            minSize: 220,
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                const description = row.original.objectDescription
                const hoverText = [objectID, description].filter(Boolean).join(' — ')
                return (
                    <div className="flex flex-col min-w-0" title={hoverText || undefined}>
                        <span className="text-sm font-medium truncate">{objectID || '-'}</span>
                        {description && (
                            <span className="text-xs text-muted-foreground truncate">{description}</span>
                        )}
                    </div>
                )
            },
        },
        {
            id: 'objectIcon',
            header: '',
            size: 48,
            minSize: 48,
            maxSize: 48,
            meta: { compact: true },
            cell: ({ row }) => {
                const iconFilename = row.original.iconFilename
                if (!iconFilename) return null
                return (
                    <div className="flex items-center justify-center">
                        <EntityIcon src={buildIconPath(iconFilename)} alt="" className="h-8 w-8" />
                    </div>
                )
            },
        },
        {
            id: 'scenarioInfo',
            header: '',
            size: 24,
            minSize: 22,
            maxSize: 28,
            meta: { compact: true },
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                const description = row.original.objectDescription
                const requestID = row.original.requestID
                const scenario = row.original.scenario
                if (!onShowScenarioInfo || requestID == null) return <div className="flex items-center justify-center p-0">-</div>
                return (
                    <div className="flex items-center justify-center p-0">
                        <RowScenarioInfoIcon
                            requestID={requestID}
                            scenario={scenario}
                            locale={locale}
                            t={t}
                            onOpenModal={() => onShowScenarioInfo(requestID, scenario, objectID, description)}
                        />
                    </div>
                )
            },
        },
        {
            id: 'qrCode',
            header: '',
            size: 24,
            minSize: 22,
            maxSize: 28,
            meta: { compact: true },
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                if (!objectID || !onShowQRCode) return <div className="flex items-center justify-center p-0">-</div>
                return (
                    <div className="flex items-center justify-center p-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 min-w-0"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowQRCode(objectID)
                            }}
                            title={t('objects.showQRCode')}
                        >
                            <QrCode className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )
            },
        },
        {
            id: 'subObjects',
            header: '',
            size: 24,
            minSize: 22,
            maxSize: 28,
            meta: { compact: true },
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                const requestID = row.original.requestID
                const requestObjectCount = row.original.requestObjectCount || 0
                if (!objectID || !requestID || !onShowObjectDetails || requestObjectCount <= 1) {
                    return <div className="flex items-center justify-center p-0">-</div>
                }
                return (
                    <div className="flex items-center justify-center p-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 min-w-0"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowObjectDetails(objectID, requestID)
                            }}
                            title={t('recoverRequests.viewObjectDetails')}
                        >
                            <FileStack className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )
            },
        },
        {
            accessorKey: 'versionMetadata',
            header: '',
            size: 24,
            minSize: 22,
            maxSize: 28,
            meta: { compact: true },
            cell: ({ row }) => {
                const metadata = row.getValue('versionMetadata') as string | null
                if (!metadata) return <div className="flex items-center justify-center p-0"><span className="text-muted-foreground">-</span></div>
                return (
                    <div className="flex items-center justify-center p-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 min-w-0"
                            onClick={() => onShowMetadata?.(t('recoverRequests.metadata'), metadata)}
                        >
                            <FileJson className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )
            },
        },
        {
            accessorKey: 'versionAnalytics',
            header: t('recoverRequests.analytics'),
            size: 70,
            minSize: 60,
            maxSize: 80,
            cell: ({ row }) => {
                const analytics = row.getValue('versionAnalytics') as number | null
                return (
                    <div className="flex items-center justify-center">
                        {getStatusCircle(analytics)}
                    </div>
                )
            },
        },
        {
            accessorKey: 'requestDateTime',
            header: t('recoverRequests.requestDateTime'),
            size: 140,
            minSize: 120,
            maxSize: 160,
            cell: ({ row }) => {
                const dateTime = row.getValue('requestDateTime') as string | Date | null
                return (
                    <span className="text-sm whitespace-nowrap">
                        {dateTime ? formatDate(dateTime, locale) : '-'}
                    </span>
                )
            },
        },
        {
            accessorKey: 'location',
            header: t('recoverRequests.location'),
            size: 150,
            minSize: 120,
            maxSize: 180,
            cell: ({ row }) => {
                const location = row.getValue('location') as string | null
                return <span className="text-sm">{location || '-'}</span>
            },
        },
        {
            accessorKey: 'teamName',
            header: t('recoverRequests.teamName'),
            size: 150,
            minSize: 120,
            maxSize: 200,
            cell: ({ row }) => {
                const teamName = row.getValue('teamName') as string | null
                return <span className="text-sm">{teamName || '-'}</span>
            },
        },
        {
            accessorKey: 'forecastStartTime',
            header: t('recoverRequests.forecastStartTime'),
            size: 140,
            minSize: 120,
            maxSize: 160,
            cell: ({ row }) => {
                // THE FORECAST HAS TO AGREE WITH THE BUTTON. The stored ForecastStartTime walks
                // every dependency edge, so an object marked Assign because its remaining
                // dependencies are all bypassable was showing a start time hours out — the row
                // told the reader to start now and to wait, at the same time. Under the bypass
                // policy the row shows the forecast computed over hard edges only.
                const strict = row.getValue('forecastStartTime') as string | Date | null
                const bypassed = row.original.forecastStartTimeBypassable ?? null
                const shown = allowBypass && bypassed ? bypassed : strict
                const moved = allowBypass && bypassed != null && strict != null
                    && new Date(bypassed).getTime() !== new Date(strict).getTime()
                return (
                    <span
                        className="text-sm whitespace-nowrap"
                        title={moved
                            ? `${t('recoverRequests.forecastWithoutBypass')}: ${formatDate(strict, locale)}`
                            : undefined}
                    >
                        {shown ? formatDate(shown, locale) : '-'}
                        {moved && <span className="ml-1 text-amber-600" aria-hidden>*</span>}
                    </span>
                )
            },
        },
        {
            accessorKey: 'objectReady',
            header: t('recoverRequests.objectReady'),
            size: 110,
            minSize: 90,
            maxSize: 130,
            cell: ({ row }) => {
                const objectReady = row.getValue('objectReady') as number | null
                // Debug logging
                if (process.env.NODE_ENV === 'development') {
                    console.log('🔍 DEBUG ObjectReady column:', {
                        objectReady,
                        objectReadyType: typeof objectReady,
                        rowData: row.original
                    })
                }
                if (objectReady === null || objectReady === undefined) {
                    return <span className="text-sm text-muted-foreground">-</span>
                }
                const isReady = objectReady === 1
                return (
                    <span className={`text-sm ${isReady ? 'text-green-600' : 'text-amber-600'}`}>
                        {isReady ? t('recoverRequests.objectReadyStatus') : t('recoverRequests.copyInProgress')}
                    </span>
                )
            },
        },
        {
            accessorKey: 'waitGo',
            header: t('recoverRequests.status'),
            size: 150,
            minSize: 130,
            maxSize: 170,
            meta: { compact: true },
            cell: ({ row }) => {
                const waitGo = row.getValue('waitGo') as string | null
                const objectID = row.original.objectID
                const scenario = row.original.scenario
                const forecastStartTime = row.original.forecastStartTime
                
                if (!waitGo) return <span className="text-muted-foreground">-</span>
                // Go under the reader's own bypass policy, not just the database's.
                const isGo = effectiveIsGo(row.original, allowBypass)
                return (
                    <div className="flex items-center justify-center gap-0 h-full">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="relative inline-flex flex-shrink-0">
                                    <WaitGoCaveatBadge row={row.original} />
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`w-12 h-12 aspect-square rounded flex items-center justify-center text-sm font-medium flex-shrink-0 p-0 cursor-pointer ${
                                            isGo
                                                ? 'bg-[rgb(151,200,100)] text-white hover:bg-[rgb(151,200,100)] hover:text-white animate-assign-start-pulse'
                                                : 'bg-[rgb(232,96,55)] text-white hover:bg-[rgb(232,96,55)] hover:text-white'
                                        }`}
                                        title={waitGoTitle(row.original)}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            if (objectID && onAssignEngineer) {
                                                const recoverRequestId = row.original.id
                                                const analytics = row.original.versionAnalytics
                                                const responsibleTeamId = row.original.responsibleTeamId
                                                onAssignEngineer(objectID, recoverRequestId, waitGo, analytics, responsibleTeamId, row.original.locationId || null)
                                            }
                                        }}
                                    >
                                        {isGo ? t('recoverRequests.assign') : 'WAIT'}
                                    </Button>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{t('recoverRequests.assignEngineer')}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        {objectID && (
                            <TooltipProvider>
                                <div className="flex items-center gap-0 flex-shrink-0">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-5 p-0 min-w-0 text-blue-600 hover:text-blue-700 flex-shrink-0"
                                                onClick={() => onShowRunbook?.(objectID, scenario, forecastStartTime)}
                                            >
                                                <List className="h-3.5 w-3.5" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{t('recoverRequests.viewRunbook')}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-5 p-0 min-w-0 text-blue-600 hover:text-blue-700 flex-shrink-0"
                                                onClick={() => onShowFloorPlan?.(objectID)}
                                            >
                                                <Map className="h-3.5 w-3.5" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{t('recoverRequests.viewFloorPlan')}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    {onShowSubObjectProgress && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-5 p-0 min-w-0 text-blue-600 hover:text-blue-700 flex-shrink-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        onShowSubObjectProgress(objectID, row.original.objectDescription)
                                                    }}
                                                >
                                                    <ListTree className="h-3.5 w-3.5" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{t('recoverRequests.subObjectProgress', 'Sub-object progress')}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-5 p-0 min-w-0 text-blue-600 hover:text-blue-700 flex-shrink-0"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    if (onAssignEngineer) {
                                                        const recoverRequestId = row.original.id
                                                        const analytics = row.original.versionAnalytics
                                                        const responsibleTeamId = row.original.responsibleTeamId
                                                        onAssignEngineer(objectID, recoverRequestId, waitGo, analytics, responsibleTeamId, row.original.locationId || null)
                                                    }
                                                }}
                                            >
                                                <UserCheck className="h-3.5 w-3.5" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{t('recoverRequests.assignEngineer')}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    {onMarkComplete && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-5 p-0 min-w-0 text-green-600 hover:text-green-700 flex-shrink-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        e.preventDefault()
                                                        const recoverRequestId = row.original.id
                                                        if (objectID && recoverRequestId != null) {
                                                            onMarkComplete(objectID, row.original.objectDescription ?? null, recoverRequestId)
                                                        }
                                                    }}
                                                >
                                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{t('myAssignments.markComplete')}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                </div>
                            </TooltipProvider>
                        )}
                    </div>
                )
            },
        },
    ]
}
